import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import SmartToyIcon from '@mui/icons-material/SmartToyOutlined'
import PersonIcon from '@mui/icons-material/PersonOutline'
import {
  COULEURS_STATUT_CAMPAGNE,
  LIBELLES_STATUT_CAMPAGNE,
  decrireSegment,
  type RapportCampagne,
} from '@iobiz/shared'
import { ApiError, api } from '../../api/client.js'

/**
 * Le RAPPORT d'une campagne : ce qu'on a voulu, ce qui s'est passé, ce qu'on
 * en retient.
 *
 * ⭐ Les échecs sont regroupés PAR MOTIF. Quinze fois « numéro invalide » est
 * une information ; quinze lignes identiques n'en sont pas, et personne ne les
 * lit jusqu'au bout.
 */

const dateHeure = (iso: string): string =>
  new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })

export function RapportDialog({
  storeId,
  campagneId,
  onFermer,
}: {
  storeId: string
  campagneId: string
  onFermer: () => void
}): JSX.Element {
  const [rapport, setRapport] = useState<RapportCampagne | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  useEffect(() => {
    let vivant = true
    api
      .get<RapportCampagne>(`/stores/${storeId}/campaigns/${campagneId}/rapport`)
      .then((r) => {
        if (vivant) setRapport(r)
      })
      .catch((e: unknown) => {
        if (vivant) setErreur(e instanceof ApiError ? e.message : 'Rapport indisponible.')
      })
    return () => {
      vivant = false
    }
  }, [storeId, campagneId])

  const c = rapport?.campagne
  const traites = c ? c.sentCount + c.failedCount : 0
  const taux = traites > 0 ? Math.round(((c?.sentCount ?? 0) / traites) * 100) : 0

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onFermer}>
      <DialogTitle>{c ? c.name : 'Rapport de campagne'}</DialogTitle>

      <DialogContent dividers>
        {erreur && <Alert severity="error">{erreur}</Alert>}

        {!rapport && !erreur && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {rapport && c && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {/* ------------------------------------------------------ résumé */}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip
                size="small"
                label={LIBELLES_STATUT_CAMPAGNE[c.status]}
                color={COULEURS_STATUT_CAMPAGNE[c.status]}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {decrireSegment(c.segment)}
              </Typography>
              {c.createdByName && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  · créée par {c.createdByName}
                </Typography>
              )}
              {c.sentAt && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  · envoyée le {dateHeure(c.sentAt)}
                </Typography>
              )}
            </Box>

            {c.lastError && <Alert severity="warning">{c.lastError}</Alert>}

            <Paper variant="outlined" sx={{ p: 4, borderRadius: 3 }}>
              <Box
                sx={{
                  display: 'grid',
                  gap: 4,
                  gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
                }}
              >
                {[
                  { label: 'Destinataires', valeur: c.recipientsTotal },
                  { label: 'Messages remis', valeur: c.sentCount },
                  { label: 'Échecs', valeur: c.failedCount },
                  { label: 'Restants', valeur: c.pendingCount },
                ].map((k) => (
                  <Box key={k.label}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {k.label}
                    </Typography>
                    <Typography variant="h3" className="tabular">
                      {k.valeur}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Taux de remise
                  </Typography>
                  <Typography variant="caption" className="tabular">
                    {taux} %
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={taux}
                  color={taux >= 90 ? 'success' : taux >= 70 ? 'warning' : 'error'}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            </Paper>

            {/* ---------------------------------------------------- le message */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                Message envoyé
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {c.message}
                </Typography>
              </Paper>
            </Box>

            {/* ------------------------------------------------------- échecs */}
            {rapport.echecsParMotif.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                  Échecs par motif
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {rapport.echecsParMotif.map((e) => (
                    <Box
                      key={e.motif}
                      sx={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}
                    >
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {e.motif}
                      </Typography>
                      <Typography variant="body2" className="tabular">
                        {e.nombre}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* ---------------------------------------------------- analyse IA */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <AutoAwesomeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Analyse
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    rapport.sourceAnalyse === 'ia'
                      ? 'Assistant IA'
                      : 'Conseils intégrés (IA non configurée)'
                  }
                />
              </Box>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {rapport.analyse}
              </Typography>

              {rapport.recommandations.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Recommandations
                  </Typography>
                  <Box component="ul" sx={{ pl: 5, m: 0, mt: 1 }}>
                    {rapport.recommandations.map((r, i) => (
                      <Typography key={i} component="li" variant="body2" sx={{ mb: 1 }}>
                        {r}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            <Divider />

            {/* ------------------------------------------------------ journal */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                Historique des actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {rapport.evenements.map((e) => (
                  <Box key={e.id} sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                    {/* ⭐ L'agent et l'humain se distinguent d'un coup d'œil :
                        « suspendue » par un gérant et « suspendue » par l'agent
                        appellent des suites opposées. */}
                    {e.byAgent ? (
                      <SmartToyIcon sx={{ fontSize: 16, color: 'primary.main', mt: '3px' }} />
                    ) : (
                      <PersonIcon sx={{ fontSize: 16, color: 'text.disabled', mt: '3px' }} />
                    )}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {e.message}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                        {dateHeure(e.at)} · {e.byAgent ? 'agent' : 'utilisateur'}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                {rapport.evenements.length === 0 && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Aucune action enregistrée.
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={onFermer}>Fermer</Button>
      </DialogActions>
    </Dialog>
  )
}
